export interface TurnWatchdogTimers {
  setTimeout: (handler: () => void, ms: number) => number
  clearTimeout: (id: number) => void
  now: () => number
}

export interface TurnWatchdogOptions {
  inactivityMs: number
  absoluteMs?: number | null
  onExpire: (scopeId: string, reason: 'inactivity' | 'absolute') => void
  timers?: TurnWatchdogTimers
}

interface WatchdogEntry {
  inactivityTimerId: number
  absoluteTimerId: number | null
}

const defaultTimers: TurnWatchdogTimers = {
  setTimeout: (handler, ms) => globalThis.setTimeout(handler, ms) as unknown as number,
  clearTimeout: (id) => globalThis.clearTimeout(id as unknown as ReturnType<typeof setTimeout>),
  now: () => Date.now(),
}

/**
 * Per-turn inactivity watchdog (D-10): every backend signal for a turn resets its window,
 * and only a full window of silence expires it. One entry per scope, so turns of different
 * conversations never share or overwrite each other's timer.
 */
export class TurnWatchdog {
  private readonly entries = new Map<string, WatchdogEntry>()

  private readonly timers: TurnWatchdogTimers

  private readonly absoluteMs: number | null

  constructor(private readonly options: TurnWatchdogOptions) {
    this.timers = options.timers ?? defaultTimers
    this.absoluteMs = options.absoluteMs ?? null
  }

  start(scopeId: string): void {
    this.stop(scopeId)

    const entry: WatchdogEntry = {
      inactivityTimerId: this.timers.setTimeout(
        () => this.expire(scopeId, 'inactivity'),
        this.options.inactivityMs
      ),
      absoluteTimerId:
        this.absoluteMs === null
          ? null
          : this.timers.setTimeout(() => this.expire(scopeId, 'absolute'), this.absoluteMs),
    }

    this.entries.set(scopeId, entry)
  }

  notify(scopeId: string): void {
    const entry = this.entries.get(scopeId)
    if (!entry) return

    this.timers.clearTimeout(entry.inactivityTimerId)
    entry.inactivityTimerId = this.timers.setTimeout(
      () => this.expire(scopeId, 'inactivity'),
      this.options.inactivityMs
    )
  }

  stop(scopeId: string): void {
    const entry = this.entries.get(scopeId)
    if (!entry) return

    this.timers.clearTimeout(entry.inactivityTimerId)
    if (entry.absoluteTimerId !== null) {
      this.timers.clearTimeout(entry.absoluteTimerId)
    }
    this.entries.delete(scopeId)
  }

  stopAll(): void {
    for (const scopeId of Array.from(this.entries.keys())) {
      this.stop(scopeId)
    }
  }

  isRunning(scopeId: string): boolean {
    return this.entries.has(scopeId)
  }

  private expire(scopeId: string, reason: 'inactivity' | 'absolute'): void {
    if (!this.entries.has(scopeId)) return

    this.stop(scopeId)
    this.options.onExpire(scopeId, reason)
  }
}
